import LocalPoliceRounded from '@mui/icons-material/LocalPoliceRounded';
import PreviewRounded from '@mui/icons-material/PreviewRounded';
import VerifiedIcon from '@mui/icons-material/Verified';
import { MenuItem } from '@mui/material';
import { useState } from 'react';

import { Piece, PieceAction } from '@apiContract';
import { CustomPopover, CustomPopoverProps } from '@components/CustomPopover';
import Divider from '@components/Divider';
import { CommonAction, ValidationData } from '@components/MailpieceActions/types';
import { combineActions, getActionDisplayPriority } from '@components/MailpieceActions/utils';
import { useMailpiece } from '@hooks/useMailpiece';
import { useNavigation } from '@hooks/useNavigation';
import { useSettings } from '@hooks/useSettings';
import { MailPieceInboxType } from '@rdTypes/shared';
import { InboxMailpieceCategory, MailroomMailpieceCategory, inboxRoutes, mailroomRoutes } from '@routes';

import { openForceActionsDrawer, startAction } from '@redux/slices/mailpieceAction';
import { useDispatch, useSelector } from '@redux/store';

import { useMailpieceActions } from '../hooks/useMailpieceActions';
import { mailpieceActions } from '../index';
import { ActionValidation } from './ActionValidation';

export enum ViewActionType {
  Inbox,
  Mailroom,
}

interface MailpieceActionsMenuProps extends CustomPopoverProps {
  mailpiece: Piece;
  userAccess: MailPieceInboxType;
  openMailpieceItemCategory?: MailroomMailpieceCategory | InboxMailpieceCategory;
  onAssignClick?: (piece: Piece) => void;
  onImageClick?: (pieceId: number) => void;
  viewActionType?: ViewActionType;
  pendingSearchValue?: string;
  hideForceActions?: boolean;
}

export const MailpieceActionsMenu = ({
  mailpiece,
  userAccess,
  openMailpieceItemCategory,
  onAssignClick,
  onImageClick,
  anchorEl,
  viewActionType = ViewActionType.Mailroom,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  pendingSearchValue,
  hideForceActions = false,
  ...props
}: MailpieceActionsMenuProps) => {
  const { navigateWithPrevPath } = useNavigation();
  const dispatch = useDispatch();

  const [validationDialogAction, setValidationDialogAction] = useState<CommonAction | null>(null);

  const { currentAction } = useSelector((state) => state.mailpieceAction);

  const { regularActions, hasForceActions } = useMailpieceActions(mailpiece, userAccess);

  const validationSettings = useSettings(['PackageValidationPickup', 'PackageValidationDeliver']) as Record<
    string,
    boolean
  >;
  const parsedActions = combineActions(regularActions, validationSettings, mailpiece.pieceType);
  const sortedActions = [...parsedActions].sort((a, b) => getActionDisplayPriority(a) - getActionDisplayPriority(b));
  const menuActions = sortedActions.filter((action) => action !== PieceAction.Delete);
  const hasDeleteAction = sortedActions.includes(PieceAction.Delete);

  const { getForceActionTitle } = useMailpiece();
  const forceActionTitle = getForceActionTitle([mailpiece]);

  const handleStartAction = (action: CommonAction, validationData?: ValidationData) => {
    props.onClose?.({}, 'escapeKeyDown');

    if (action === PieceAction.Assign && onAssignClick) {
      onAssignClick(mailpiece);
    } else {
      dispatch(startAction({ action, mailpiece, pendingSearchValue, validationData }));
    }
  };

  const handleOpenForceActions = () => {
    dispatch(openForceActionsDrawer({ pieces: [mailpiece], pendingSearchValue }));
    props.onClose?.({}, 'escapeKeyDown');
  };

  const renderActionItem = (action: CommonAction) => {
    const actionConfig = mailpieceActions[action];

    if (!actionConfig) return null;

    const { title, Icon, actionTriggerColor, validationSettingName } = actionConfig;

    const validationSetting =
      typeof validationSettingName === 'function' ? validationSettingName(mailpiece) : validationSettingName;

    return (
      <ActionValidation
        key={action}
        dialogControlComponent={
          <MenuItem onClick={() => setValidationDialogAction(action)}>
            <>
              <VerifiedIcon /> Validate Item
            </>
          </MenuItem>
        }
        validationDialogOpen={validationDialogAction === action}
        closeValidationDialog={() => setValidationDialogAction(null)}
        onValidationSuccess={(validationData) => handleStartAction(action, validationData)}
        settingName={validationSetting}
        pieceType={mailpiece.pieceType}
        piecesForValidate={[mailpiece]}
      >
        {action === PieceAction.Delete && <Divider dashed />}
        <MenuItem
          onClick={() => handleStartAction(action)}
          disabled={currentAction !== null}
          sx={{ color: actionTriggerColor ? `${actionTriggerColor}.main` : 'primary' }}
        >
          <>
            <Icon /> {typeof title === 'function' ? title(mailpiece) : title}
          </>
        </MenuItem>
      </ActionValidation>
    );
  };

  return (
    <CustomPopover {...props} anchorEl={anchorEl} keepMounted anchorOrigin={anchorOrigin}>
      {openMailpieceItemCategory && (
        <MenuItem
          onClick={() =>
            navigateWithPrevPath(
              viewActionType === ViewActionType.Inbox
                ? inboxRoutes.mailpiece(openMailpieceItemCategory, mailpiece.id)
                : mailroomRoutes.mailpiece(openMailpieceItemCategory, mailpiece.id),
              { state: { previousCategory: openMailpieceItemCategory } },
            )
          }
          disabled={currentAction !== null}
        >
          <PreviewRounded /> View Details
        </MenuItem>
      )}

      {menuActions.map(renderActionItem)}

      {hasForceActions && !hideForceActions && (
        <>
          <Divider dashed />
          <MenuItem onClick={handleOpenForceActions} disabled={currentAction !== null}>
            <LocalPoliceRounded /> {forceActionTitle}
          </MenuItem>
        </>
      )}

      {hasDeleteAction && renderActionItem(PieceAction.Delete)}
    </CustomPopover>
  );
};
